import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  Calendar,
  MapPin,
  Users,
  Eye,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const FeaturedProjects = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
  const [hoveredProject, setHoveredProject] = useState(null);

  const projects = [
    {
      id: 1,
      title: "Forest Clearance for 3x800 MW OTEL USCTPP",
      category: "Forest Clearance",
      description:
        "Comprehensive DGPS survey for Forest Diversion Proposal & Wildlife clearance for 3x800 MW thermal power plant as per MoEF & CC Guidelines & Forest (Conservation) Act 1980. Major infrastructure project in Cuttack, Odisha.",
      image:
        "https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      technologies: [
        "DGPS Survey",
        "Wildlife Assessment",
        "Environmental Impact",
        "GIS Analysis",
        "Thermal Mapping",
      ],
      duration: "12 Months",
      location: "Cuttack, Odisha",
      client: "Orissa Thermal Energy Ltd.",
      year: "2024",
      liveLink: "#",
      githubLink: "#",
      status: "Ongoing",
      progress: 75,
      significance: "Major Infrastructure • Environmental Compliance"
    },
    {
      id: 2,
      title: "SAIL-RMD Manoharpur Ore Mines Survey",
      category: "Total Station Survey",
      description:
        "High-precision Total Station and DGPS survey of 15.333 km Ropecon conveyor route boundary at SAIL's mining operations in Chiria, Jharkhand. Advanced 3D modeling for conveyor alignment and infrastructure planning.",
      image:
        "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      technologies: [
        "Total Station",
        "DGPS",
        "3D Modeling",
        "Conveyor Alignment",
        "Mine Planning",
      ],
      duration: "8 Months",
      location: "Chiria, Jharkhand",
      client: "SAIL-RMD",
      year: "2023",
      liveLink: "#",
      githubLink: "#",
      status: "Completed",
      progress: 100,
      significance: "Large-scale Mining • Precision Survey"
    },
    {
      id: 3,
      title: "IIT Bhubaneswar Campus Survey",
      category: "DGPS Survey",
      description:
        "Detailed survey of forest land proposed for diversion for establishment of Permanent Campus of IIT Bhubaneswar. Critical project for educational infrastructure development in Khurda district.",
      image:
        "https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&auto=format&fit=crop&w=2064&q=80",
      technologies: [
        "DGPS",
        "Campus Planning",
        "Site Analysis",
        "Educational Infrastructure",
        "Land Use Planning",
      ],
      duration: "6 Months",
      location: "Argul, Jatani, Khurda",
      client: "IIT Bhubaneswar",
      year: "2023",
      liveLink: "#",
      githubLink: "#",
      status: "Completed",
      progress: 100,
      significance: "Educational Infrastructure • National Importance"
    },
    {
      id: 4,
      title: "NTFP Inventory Mapping - 8 Forest Divisions",
      category: "GIS Mapping",
      description:
        "Customized interactive GIS tool for Non-Timber Forest Produce (NTFP) inventory work across 8 forest divisions. Integrated mobile GIS and database management for forest resource conservation.",
      image:
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80",
      technologies: [
        "ArcGIS",
        "Web GIS",
        "Mobile Mapping",
        "Database Management",
        "NTFP Inventory",
      ],
      duration: "12 Months",
      location: "Multiple Forest Divisions",
      client: "Forest Department",
      year: "2023",
      liveLink: "#",
      githubLink: "#",
      status: "Completed",
      progress: 100,
      significance: "Forest Conservation • Technology Integration"
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "from-green-500 to-emerald-500";
      case "In Progress":
        return "from-blue-500 to-cyan-500";
      case "Ongoing":
        return "from-orange-500 to-amber-500";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "Forest Clearance":
        return "from-green-600 to-emerald-600";
      case "DGPS Survey":
        return "from-blue-600 to-cyan-600";
      case "Total Station Survey":
        return "from-purple-600 to-pink-600";
      case "GIS Mapping":
        return "from-orange-600 to-amber-600";
      default:
        return "from-gray-600 to-gray-700";
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.8,
      },
    },
  };

  const imageVariants = {
    hidden: { scale: 1 },
    hover: {
      scale: 1.05,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
  };

  return (
    <section ref={sectionRef} className="relative py-6 lg:py-12 bg-white">
      {/* Decorative Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 w-64 h-64 bg-brandBlue rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-brandGreen rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section with enhanced animations */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 20,
            duration: 1,
          }}
          className="text-center max-w-4xl mx-auto mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={
              isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-brandBlue/10 to-brandGreen/10 border border-brandBlue/20 mb-4"
          >
            <Eye className="w-4 h-4 text-brandBlue mr-2" />
            <span className="text-sm font-semibold text-brandText">
              Featured Projects
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{
              type: "spring",
              stiffness: 80,
              damping: 20,
              delay: 0.5,
              duration: 1,
            }}
            className="text-3xl flex flex-col sm:flex-row items-center justify-center gap-4 lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-8 font-lexend leading-tight"
          >
            <span>Our</span>
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={
                isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }
              }
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 15,
                delay: 0.7,
                duration: 0.8,
              }}
              className="block bg-gradient-to-r from-brandBlue to-brandGreen bg-clip-text text-transparent"
            >
              Portfolio
            </motion.span>
          </motion.h2>

          <p className="text-lg lg:text-xl text-secondaryTextColor leading-relaxed">
            Showcasing our most impactful projects in GIS, Surveying, and Environmental Services
          </p>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 gap-8"
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              variants={cardVariants}
              onHoverStart={() => setHoveredProject(project.id)}
              onHoverEnd={() => setHoveredProject(null)}
              className="group relative bg-white rounded-3xl border border-gray-200/80 hover:border-brandBlue/30 transition-all duration-700 hover:shadow-2xl overflow-hidden"
            >
              {/* Project Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <motion.img
                  src={project.image}
                  alt={project.title}
                  variants={imageVariants}
                  initial="hidden"
                  whileHover="hover"
                  className="w-full h-full object-cover transition-transform duration-700"
                />
                
                {/* Overlay Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-t ${getCategoryColor(project.category)} opacity-10 group-hover:opacity-20 transition-opacity duration-700`}></div>

                {/* Significance Tag */}
                <div className="absolute top-4 right-4 z-10">
                  <span className="px-3 py-1 rounded-full text-white text-xs font-semibold backdrop-blur-sm bg-black/70 shadow-lg">
                    {project.significance}
                  </span>
                </div>

                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col space-y-2 z-10">
                  <span
                    className={`px-4 py-2 rounded-2xl text-white text-sm font-semibold ${getCategoryColor(
                      project.category
                    )} backdrop-blur-sm shadow-lg`}
                  >
                    {project.category}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-white text-xs font-medium ${getStatusColor(
                      project.status
                    )} backdrop-blur-sm shadow-lg`}
                  >
                    {project.status}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="absolute bottom-4 left-4 right-4 z-10">
                  <div className="flex items-center justify-between mb-2 text-white text-xs bg-black/40 backdrop-blur-sm rounded-full px-2 py-1">
                    <span>Project Progress</span>
                    <span>{project.progress}%</span>
                  </div>
                  <div className="w-full bg-white/30 rounded-full h-2">
                    <motion.div
                      className={`h-2 rounded-full ${getStatusColor(
                        project.status
                      )}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${project.progress}%` }}
                      transition={{ duration: 1.2, delay: index * 0.2 }}
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 lg:p-8">
                <h3 className="text-2xl font-bold text-primaryTextColor mb-3 group-hover:text-brandBlue transition-colors">
                  {project.title}
                </h3>
                <p className="text-secondaryTextColor mb-6">
                  {project.description}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center space-x-3 p-3 rounded-xl bg-gray-50 hover:bg-blue-50 transition-all">
                    <Calendar className="w-5 h-5 text-brandBlue" />
                    <div>
                      <div className="text-sm text-secondaryTextColor">
                        Duration
                      </div>
                      <div className="font-semibold">{project.duration}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-xl bg-gray-50 hover:bg-green-50 transition-all">
                    <MapPin className="w-5 h-5 text-brandBlue" />
                    <div>
                      <div className="text-sm text-secondaryTextColor">
                        Location
                      </div>
                      <div className="font-semibold">{project.location}</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-6 border p-3 rounded-xl bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-green-50 transition-all">
                  <div className="flex items-center space-x-3">
                    <Users className="w-4 h-4 text-brandBlue" />
                    <div>
                      <div className="text-sm text-secondaryTextColor">
                        Client
                      </div>
                      <div className="font-semibold">{project.client}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-secondaryTextColor">Year</div>
                    <div className="font-bold text-brandBlue">
                      {project.year}
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-semibold mb-3 uppercase tracking-wide text-primaryTextColor">
                    Technologies Used
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <motion.span
                        key={techIndex}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: techIndex * 0.1 }}
                        className="px-4 py-2 bg-gradient-to-br from-gray-100 to-gray-200 text-sm font-medium rounded-xl hover:bg-brandBlue hover:text-white transition-all cursor-pointer hover:scale-105"
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-4 bg-gradient-to-r from-brandGreen to-brandBlue hover:from-brandBlue hover:to-brandGreen text-white font-semibold rounded-xl transition-all flex items-center justify-center space-x-3 hover:shadow-lg"
                >
                  <span>View Project Details</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-12 text-center"
        >
          <p className="text-lg text-secondaryTextColor mb-6">
            Want to see more of our work?
          </p>
          <motion.a
            onClick={()=>navigate('/projects')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-brandBlue to-brandGreen text-white font-semibold rounded-xl hover:shadow-xl transition-all"
          >
            View All Projects
            <ChevronRight className="w-5 h-5 ml-2" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProjects;